/**
 * ShareholderPopup - Full-Screen Popup Orchestrator Component
 *
 * Composes the full-screen shareholder view:
 * - Backdrop overlay
 * - Header with title and close button
 * - ShareholderLegend (popup variant) and ShareholderFilters (popup variant)
 * - ShareholderGraph (popup variant: the wheel zooms; its toolbar has Fit, Lock layout, Reset
 *   layout and Download)
 *
 * Opens from the tab's Full screen button on desktop and from 'Open graph' on phones.
 *
 * @module ShareholderPopup
 */

import PropTypes from 'prop-types';
import IconButton, { ICON_BUTTON_ICONS } from '../../../buttons/IconButton';
import ShareholderFilters from './ShareholderFilters';
import ShareholderGraph from './ShareholderGraph';
import ShareholderLegend from './ShareholderLegend';

/**
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether popup is visible
 * @param {Object|null} props.graph - PreparedGraph (useShareholderState.graph)
 * @param {'idle'|'loading'|'loaded'|'failed'} [props.figuresStatus] - card figure state
 * @param {string} [props.companyName] - PNG file name
 * @param {number} [props.percentageFilter] - applied minimum share (no-owners message)
 * @param {Object} props.filters - Pending filter values
 * @param {Object} props.validationMessages - Validation error messages
 * @param {boolean} props.showOnSubmitErrorState - Show validation errors on submit
 * @param {boolean} props.isFilterDisabled - Whether the apply button is disabled
 * @param {boolean} props.isClearDisabled - Whether the clear button is disabled
 * @param {Function} props.onClose - Close the popup
 * @param {Function} props.onFilterChange - Filter value change
 * @param {Function} props.onApply - Apply filters
 * @param {Function} props.onClear - Clear filters
 * @param {Function} [props.getNodeHref] - (raw, node) => href of a graph node's page
 * @param {Function} [props.onViewNodeDetails] - (raw, node) => same-tab navigation
 * @param {Function} [props.onOpenNodeInNewTab] - (raw, node) => open in a new tab
 * @param {Function} [props.onToggleNodeCollapsed] - (nodeId) => collapse/expand owners
 * @param {Function} [props.onShowAllFiltered] - 'Show all' on the aggregate
 * @param {boolean} [props.isLayoutLocked] - Lock layout pressed (shared with the tab graph)
 * @param {Function} [props.onToggleLayoutLock] - the toolbar's Lock layout toggle
 * @param {boolean} [props.canExport] - false disables Download
 * @param {boolean} [props.isExportDisabled] - the profilerexportdisabled right (tooltip text)
 * @param {Function} [props.onExportError] - (message) => show a failed PNG export
 * @param {Function} [props.onConfirmDownload] - () => Promise<DOWNLOAD_VERDICT>: meters the
 *   PNG download once the image exists; only a recorded download is saved
 */
const ShareholderPopup = ({
  isOpen = false,
  graph = null,
  figuresStatus = 'loaded',
  companyName = '',
  percentageFilter,
  filters = {},
  validationMessages = {},
  showOnSubmitErrorState = false,
  isFilterDisabled = false,
  isClearDisabled = false,
  onClose,
  onFilterChange,
  onApply,
  onClear,
  getNodeHref,
  onViewNodeDetails,
  onOpenNodeInNewTab,
  onToggleNodeCollapsed,
  onShowAllFiltered,
  isLayoutLocked,
  onToggleLayoutLock,
  canExport = true,
  isExportDisabled = false,
  onExportError,
  onConfirmDownload
}) => {
  if (!isOpen) {
    return null;
  }

  return (
    <>
      {/* Backdrop overlay: for the pointer only. It shows no focus, so the keyboard closes the
          popup with the X in the header instead. */}
      <button
        type="button"
        className={`shareholder-structure-watchlist-popup-closer ${isOpen ? 'visible' : ''}`}
        tabIndex={-1}
        aria-label="Close popup"
        onClick={onClose}
      />

      {/* Popup container */}
      <div className={`shareholder-structure-watchlist-popup ${isOpen ? 'visible' : ''}`}>
        {/* Header */}
        <div className="sswp-header">
          <div className="sswp-title">Shareholder structure</div>
          <IconButton
            accessible
            id="sswp-close"
            ariaLabel="Close"
            icon={ICON_BUTTON_ICONS.X_LG}
            withBorder={false}
            withFill={false}
            onClick={onClose}
          />
        </div>

        {/* Content container */}
        <div className="content-container">
          {/* Left side: Legend + Filters */}
          <div className="left-container">
            <ShareholderLegend variant="popup" />

            <ShareholderFilters
              variant="popup"
              filters={filters}
              validationMessages={validationMessages}
              showOnSubmitErrorState={showOnSubmitErrorState}
              isFilterDisabled={isFilterDisabled}
              isClearDisabled={isClearDisabled}
              onFilterChange={onFilterChange}
              onApply={onApply}
              onClear={onClear}
            />
          </div>

          {/* Right side: Graph */}
          <div className="sswp-graph">
            <ShareholderGraph
              graph={graph}
              variant="popup"
              companyName={companyName}
              figuresStatus={figuresStatus}
              percentageFilter={percentageFilter}
              getNodeHref={getNodeHref}
              onViewNodeDetails={onViewNodeDetails}
              onOpenNodeInNewTab={onOpenNodeInNewTab}
              onToggleNodeCollapsed={onToggleNodeCollapsed}
              onShowAllFiltered={onShowAllFiltered}
              isLayoutLocked={isLayoutLocked}
              onToggleLayoutLock={onToggleLayoutLock}
              canExport={canExport}
              isExportDisabled={isExportDisabled}
              onExportError={onExportError}
              onConfirmDownload={onConfirmDownload}
            />
          </div>
        </div>
      </div>
    </>
  );
};

ShareholderPopup.propTypes = {
  isOpen: PropTypes.bool,
  graph: PropTypes.object,
  figuresStatus: PropTypes.oneOf(['idle', 'loading', 'loaded', 'failed']),
  companyName: PropTypes.string,
  percentageFilter: PropTypes.number,
  filters: PropTypes.object,
  validationMessages: PropTypes.object,
  showOnSubmitErrorState: PropTypes.bool,
  isFilterDisabled: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
  isClearDisabled: PropTypes.bool,
  onClose: PropTypes.func,
  onFilterChange: PropTypes.func,
  onApply: PropTypes.func,
  onClear: PropTypes.func,
  getNodeHref: PropTypes.func,
  onViewNodeDetails: PropTypes.func,
  onOpenNodeInNewTab: PropTypes.func,
  onToggleNodeCollapsed: PropTypes.func,
  onShowAllFiltered: PropTypes.func,
  isLayoutLocked: PropTypes.bool,
  onToggleLayoutLock: PropTypes.func,
  canExport: PropTypes.bool,
  isExportDisabled: PropTypes.bool,
  onExportError: PropTypes.func,
  onConfirmDownload: PropTypes.func
};

export default ShareholderPopup;
